import type { ChapterData } from "./content-types";

/**
 * CAPÍTULO 4 — "Turno de noche" (Torre Central, oficinas).
 * Mecánica distintiva: AJUSTAR EL PUESTO (ergo). Misión SIN EPIs:
 * en oficina la protección es organizativa.
 */
export const TORRE: ChapterData = {
  id: "torre",
  title: "Turno de noche",
  location: "torre",
  secStart: 40,
  secGreat: 84,
  secOk: 70,
  secBad: 60,
  needTalk: 2,
  needHazards: 3,

  interactables: [
    { id: "puesto", type: "machine", pos: { x: 5, y: 5 }, label: "Puesto de trabajo", verb: "use", ref: "puesto", radius: 1.7, color: 0x8a6f45 },
    { id: "pantalla", type: "object", pos: { x: 5, y: 3 }, label: "Pantalla del puesto", verb: "inspect", ref: "pantalla", radius: 1.4, color: 0x6a86a0 },
    { id: "silla", type: "object", pos: { x: 3, y: 4 }, label: "Silla", verb: "inspect", ref: "silla", radius: 1.4, color: 0x4a545a },
    { id: "luz", type: "object", pos: { x: 7, y: 2 }, label: "Ventana e iluminación", verb: "inspect", ref: "luz", radius: 1.4, color: 0xbfe0ea },
    { id: "rota", type: "object", pos: { x: 11, y: 3 }, label: "Cuadrante de turnos", verb: "read", ref: "rota", radius: 1.4, color: 0xf4ead0 },
    { id: "carga", type: "object", pos: { x: 12.5, y: 5 }, label: "Bandeja de tareas", verb: "inspect", ref: "carga", radius: 1.4, color: 0xd9c7a0 },
    { id: "muneca", type: "object", pos: { x: 8, y: 5 }, label: "Muñequera", verb: "inspect", ref: "muneca", radius: 1.3, color: 0xd8dee6 },
    { id: "aire", type: "object", pos: { x: 9, y: 2 }, label: "Climatización", verb: "inspect", ref: "aire", radius: 1.4, color: 0xeef2f6 },
    { id: "aplicar", type: "machine", pos: { x: 12.5, y: 8 }, label: "Aplicar las medidas", verb: "use", ref: "aplicar", radius: 1.6, color: 0x1c6b64 },
    { id: "marta", type: "npc", pos: { x: 5, y: 8 }, label: "Marta", verb: "talk", ref: "marta", radius: 1.5, color: 0x7a6a86 },
    { id: "roberto", type: "npc", pos: { x: 9, y: 8 }, label: "Roberto", verb: "talk", ref: "roberto", radius: 1.5, color: 0x3a4a63 },
    { id: "vega", type: "npc", pos: { x: 13, y: 9 }, label: "Vega", verb: "talk", ref: "vega", radius: 1.5, color: 0x2f2f36 },
    { id: "salida", type: "door", pos: { x: 13, y: 10 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    puesto: {
      label: "Puesto de trabajo",
      text: "El <b>puesto</b> de Marta: silla fija, portátil sobre la mesa sin soporte, la pantalla muy baja. Todo obliga a forzar la postura. Hay que <b>ajustarlo</b>.",
      comment: "Este puesto está pensado para hacer daño despacio. Vamos a arreglarlo.",
    },
    pantalla: {
      label: "Pantalla del puesto",
      text: "Un portátil <b>sobre la mesa, sin soporte</b>. La pantalla queda muy baja: agachar el cuello todo el turno. El borde superior debería ir a la altura de los ojos.",
      note: { section: "pistas", heading: "Pantalla demasiado baja", body: "Portátil sin elevador. Mirar hacia abajo horas fuerza el cuello. La pantalla va a la altura de los ojos y a un brazo." },
      hazardId: "h_pantalla",
      comment: "Un portátil a pelo sobre la mesa. Ocho horas mirando hacia abajo: el cuello lo paga.",
    },
    silla: {
      label: "Silla",
      text: "Una silla <b>fija, sin regulación</b>. Los pies no llegan al suelo y la espalda no apoya.",
      note: { section: "pistas", heading: "Silla no regulable", body: "Sillas fijas sustituyeron a las regulables. Sin ajustar altura y respaldo, la postura se fuerza todo el turno." },
      hazardId: "h_silla",
      comment: "Una silla que no se regula. Una silla para todos no es para nadie.",
      assess: {
        identify: {
          q: "¿Qué riesgo genera esta silla fija sin regulación?",
          options: [
            { text: "Trastornos musculoesqueléticos por postura forzada mantenida", ok: true },
            { text: "Riesgo eléctrico", ok: false },
            { text: "Riesgo de caída a distinto nivel", ok: false },
          ],
          hint: "Los pies no llegan al suelo y la espalda no apoya, ocho horas al día. Piensa qué se resiente con esa postura mantenida.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Dotar de una silla regulable en altura y respaldo, adaptada a la persona", tier: "colectiva", grade: "best", why: "El puesto se adapta a la persona: una silla regulable permite pies apoyados, muslos horizontales y espalda sostenida. Es la solución de fondo." },
            { text: "Programar pausas activas y cambios de postura frecuentes", tier: "organizativa", grade: "ok", why: "Las pausas ayudan a descargar, pero no corrigen una silla que obliga a forzar la postura todo el turno." },
            { text: "Poner un cojín encima y que aguante", tier: "ninguna", grade: "bad", why: "Un apaño no da respaldo lumbar ni altura correcta.", danger: "Semana a semana, el dolor de cuello y espalda se cronifica hasta acabar en baja." },
          ],
        },
        apply: {
          intro: "Ajusta el puesto a la persona. Haz los pasos en orden.",
          steps: [
            "Sustituir la silla fija por una regulable",
            "Ajustar la altura: pies apoyados y muslos horizontales",
            "Regular el respaldo para que apoye la zona lumbar",
            "Ajustar los reposabrazos a la altura del teclado",
          ],
          wrong: "Ese paso todavía no. Primero la silla regulable; luego se ajusta de abajo arriba: altura, respaldo y reposabrazos.",
          success: "Puesto ajustado a la persona: postura neutra sin forzar todo el turno.",
        },
        note: { section: "expedientes", heading: "La silla se adapta a la persona", body: "Adaptar el puesto (silla regulable) es la medida de fondo frente a los trastornos musculoesqueléticos. Las pausas complementan; los apaños no bastan." },
      },
    },
    luz: {
      label: "Ventana e iluminación",
      text: "Las <b>persianas se quitaron</b> y un fluorescente <b>parpadea</b>. Reflejos en la pantalla: fatiga visual.",
      note: { section: "pistas", heading: "Deslumbramiento y reflejos", body: "Sin persianas y con luz parpadeante hay reflejos y fatiga visual. La iluminación debe ser suficiente y sin deslumbrar." },
      hazardId: "h_luz",
      comment: "Persianas fuera y un tubo que parpadea. La vista se cansa solo de estar aquí.",
      instrument: {
        device: "i_luxometro",
        button: "Medir la iluminación",
        kind: "gauge",
        from: 120,
        to: 190,
        unit: "lx",
        limit: 500,
        danger: true,
        reading: "190 lx en la mesa, y con reflejos. Muy por debajo de los ~500 lx recomendados para trabajar con pantallas: hay que reponer persianas, cambiar el tubo y evitar deslumbramientos.",
        note: { section: "pistas", heading: "Medición: 190 lx", body: "El luxómetro marca 190 lx en el plano de trabajo, con reflejos. Por debajo de los ~500 lx recomendados para tareas de oficina con pantalla." },
      },
      assess: {
        identify: {
          q: "¿Qué riesgo hay con los reflejos y el fluorescente parpadeante?",
          options: [
            { text: "Fatiga visual por deslumbramiento e iluminación deficiente", ok: true },
            { text: "Riesgo de incendio del fluorescente", ok: false },
            { text: "Riesgo de atrapamiento", ok: false },
          ],
          hint: "El luxómetro marca 190 lx y hay reflejos en la pantalla. Piensa qué le pasa a la vista forzada así hora tras hora.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Reponer las persianas, cambiar el tubo y orientar la pantalla sin reflejos", tier: "colectiva", grade: "best", why: "Se corrige el deslumbramiento en origen: luz suficiente y estable, sin reflejos. Es la solución de fondo del puesto." },
            { text: "Situar la pantalla perpendicular a la ventana mientras tanto", tier: "organizativa", grade: "ok", why: "Reorientar la pantalla ayuda, pero sin persianas ni luz estable el problema persiste: mejor corregir la iluminación." },
            { text: "Subir el brillo de la pantalla al máximo", tier: "ninguna", grade: "bad", why: "Más brillo no quita el reflejo y cansa más la vista.", danger: "Al final del turno: ojos secos, dolor de cabeza y visión borrosa." },
          ],
        },
        apply: {
          intro: "Corrige la iluminación del puesto. Haz los pasos en orden.",
          steps: [
            "Reponer las persianas o estores en la ventana",
            "Sustituir el fluorescente que parpadea",
            "Orientar la pantalla perpendicular a la ventana",
            "Comprobar con el luxómetro que no quedan reflejos",
          ],
          wrong: "Ese paso todavía no. Primero se controla la luz y la pantalla; medir es lo último para confirmar.",
          success: "Luz suficiente y estable, sin reflejos: la vista deja de forzarse.",
        },
        note: { section: "expedientes", heading: "Luz suficiente y sin deslumbrar", body: "Corregir la iluminación en origen (persianas, luminaria en buen estado, pantalla bien orientada) es la medida de fondo frente a la fatiga visual." },
      },
    },
    rota: {
      label: "Cuadrante de turnos",
      text: "Turnos <b>encadenados</b>, noches seguidas, <b>sin descansos</b> marcados. Meses así 'por un pico de trabajo'.",
      note: { section: "expedientes", heading: "Jornada sin pausas", body: "Turnos encadenados, trabajo nocturno y sin pausas planificadas. La organización del tiempo también es prevención." },
      hazardId: "h_jornada",
      comment: "Noche tras noche, sin una pausa marcada. La fatiga no aparece en el parte, pero está.",
    },
    carga: {
      label: "Bandeja de tareas",
      text: "Una <b>montaña de tareas</b> y avisos en rojo, todos 'urgentes'. Esa presión constante es <b>carga mental</b>.",
      note: { section: "pistas", heading: "Carga mental y presión", body: "Trabajo acumulado, todo 'urgente', sin priorizar. La sobrecarga mental sostenida es un riesgo psicosocial." },
      hazardId: "h_carga",
      comment: "Todo urgente, todo a la vez. Cuando todo es prioritario, nada lo es.",
    },
    muneca: {
      label: "Muñequera",
      text: "Una <b>muñequera</b> olvidada en la mesa. Teclear y usar el ratón en mala postura, hora tras hora, pasa factura.",
      note: { section: "objetos", heading: "Molestias por repetición", body: "Muñequera de uso diario: señal de trastorno por movimientos repetitivos. Reposamuñecas, pausas y buena postura lo previenen." },
      hazardId: "h_muneca",
      comment: "Una muñequera en la mesa. El ratón y el teclado, mal usados, también lesionan.",
    },
    aire: {
      label: "Climatización",
      text: "El <b>aire está apagado</b> 'para ahorrar'. El termómetro marca 28º. Con calor, la concentración cae y la fatiga sube.",
      note: { section: "pistas", heading: "Temperatura inadecuada", body: "Climatización apagada por ahorro: 28º y aire cargado. El confort térmico influye en la fatiga y en los errores." },
      hazardId: "h_temp",
      comment: "Veintiocho grados y el aire apagado 'por ahorrar'. Ahorras en la factura y gastas en bajas.",
      instrument: {
        device: "i_termometro",
        button: "Medir la temperatura",
        kind: "gauge",
        from: 24,
        to: 28,
        unit: "°C",
        limit: 27,
        danger: true,
        reading: "28 °C y aire cargado. Por encima del rango recomendado para trabajo de oficina (17-27 °C): sube la fatiga y los errores. Hay que restablecer la climatización.",
        note: { section: "pistas", heading: "Medición: 28 °C", body: "El termómetro marca 28 °C con el aire apagado. Por encima del rango de 17-27 °C recomendado para trabajos sedentarios de oficina." },
      },
      assess: {
        identify: {
          q: "¿Qué riesgo añade la oficina a 28 °C con el aire apagado?",
          options: [
            { text: "Fatiga y más errores por disconfort térmico", ok: true },
            { text: "Riesgo biológico", ok: false },
            { text: "Riesgo de atrapamiento", ok: false },
          ],
          hint: "El termómetro marca 28 °C, por encima del rango de confort. Piensa cómo rinde la cabeza con calor y aire cargado durante horas.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Restablecer la climatización a una temperatura de confort (17-27 °C)", tier: "colectiva", grade: "best", why: "Controlar la temperatura del local es la medida de fondo: el confort térmico reduce la fatiga y los errores para todos." },
            { text: "Repartir ventiladores y ventilar en las horas frescas", tier: "organizativa", grade: "ok", why: "Mueve el aire y alivia algo, pero no baja la carga térmica real de la sala: es un parche temporal." },
            { text: "Que cada cual se apañe y siga trabajando", tier: "ninguna", grade: "bad", why: "Ignorar el calor traslada el problema a las personas.", danger: "A media tarde, con 28 °C, la concentración cae y se disparan los errores y las molestias." },
          ],
        },
        apply: {
          intro: "Devuelve la oficina al confort térmico. Haz los pasos en orden.",
          steps: [
            "Encender la climatización",
            "Fijar la consigna dentro del rango de confort (17-27 °C)",
            "Revisar filtros y la ventilación de la sala",
            "Comprobar con el termómetro que baja la temperatura",
          ],
          wrong: "Ese paso todavía no. Primero se pone en marcha y se ajusta; medir es lo último para confirmar.",
          success: "Temperatura de confort restablecida: menos fatiga y menos errores.",
        },
        note: { section: "expedientes", heading: "Confort térmico en oficina", body: "Mantener la temperatura del local en el rango de confort (17-27 °C) es la medida de fondo. Ventiladores y ventilación solo complementan." },
      },
    },
    aplicar: {
      label: "Aplicar las medidas",
      text: "El plan de mejora del puesto: reponer persianas, sillas regulables, pausas en el cuadrante y priorizar la carga. Con todo listo, se aplica.",
      comment: "Aquí no hacían falta EPIs: hacía falta organizar y adaptar.",
    },
  },

  npcs: {
    marta: {
      name: "Marta",
      role: "Turno de noche",
      portrait: "marta",
      note: { section: "personas", heading: "Marta", body: "Trabaja el turno de noche. Dolores de cuello y muñeca, agotada. Le cambiaron la silla y le encadenan turnos." },
      dialogue: [
        { speaker: "npc", text: "¿Vienes por lo de las bajas? Ya somos varias con dolores de cuello y muñeca. Y un cansancio que no se va ni durmiendo." },
        { speaker: "epi", text: "Eso no sale de la nada. ¿Qué te duele y desde cuándo?" },
        { choices: [ { text: "¿Qué tiene mal tu puesto?", go: 3 }, { text: "¿Y los turnos, cómo los lleváis?", go: 4 } ] },
        { speaker: "npc", text: "Todo. Me quitaron la silla buena por una fija, el portátil va a pelo sobre la mesa, sin soporte, y sin persianas la pantalla es un espejo. Ocho horas con el cuello doblado. No me duele por vieja: me duele por cómo me sientan a trabajar.", flag: "marta_puesto", note: { section: "pistas", heading: "Puesto sin adaptar", body: "Marta: silla fija, portátil sin soporte con la pantalla baja y sin persianas. Postura forzada todo el turno." }, end: true },
        { speaker: "npc", text: "Noches encadenadas, sin apenas descanso entre unas y otras. 'Es un pico de trabajo', llevan diciendo meses. Aguantas hasta que el cuerpo dice basta... y entonces es cuando llega la baja que tanto les molesta.", flag: "marta_puesto", note: { section: "pistas", heading: "Jornada sin pausas", body: "Marta: turnos de noche encadenados sin descanso planificado, sostenidos meses como 'pico de trabajo'. La fatiga se acumula." }, end: true },
      ],
    },
    roberto: {
      name: "Roberto",
      role: "Jefe de equipo",
      portrait: "roberto",
      note: { section: "personas", heading: "Roberto", body: "Mando intermedio. Defiende la 'optimización': quitó sillas y persianas y alargó turnos 'temporalmente'." },
      dialogue: [
        { speaker: "npc", text: "Mira, yo cumplo con lo que me mandan. Nos pidieron 'optimizar' y optimizamos. Es temporal." },
        { speaker: "epi", text: "'Temporal'... ¿desde cuándo?" },
        { choices: [ { text: "¿Por qué cambiasteis las sillas?", go: 3 }, { text: "¿Y las pausas y los descansos?", go: 4 } ] },
        { speaker: "npc", text: "Las sillas regulables y las persianas se quitaron para recortar gastos. Vino en una lista de 'medidas de optimización'. Yo solo firmé donde me dijeron.", flag: "roberto_optimizacion", note: { section: "pistas", heading: "La 'optimización' recortó prevención", body: "Roberto: quitaron sillas regulables y persianas por una lista de 'optimización' para ahorrar." }, end: true },
        { speaker: "npc", text: "Las pausas se recortaron por el pico de trabajo. Llevamos meses así... pero es lo que hay. 'Es solo temporal', me repiten.", flag: "roberto_pausas", note: { section: "pistas", heading: "Pausas recortadas 'temporalmente'", body: "Se eliminaron pausas por un 'pico de trabajo' que dura meses. La organización del tiempo es prevención." }, end: true },
      ],
    },
    vega: {
      name: "Vega",
      role: "¿?",
      portrait: "vega",
      note: { section: "personas", heading: "Vega", body: "Confirma que las 'optimizaciones' son la lista: cada número, una medida de seguridad recortada por ahorro." },
      dialogue: [
        { speaker: "npc", text: "Once. Aquí no hay una máquina abierta ni un derrame. El riesgo es más callado. Pero está en la misma lista." },
        { speaker: "epi", text: "¿La misma lista de la fábrica y el laboratorio?" },
        { speaker: "npc", text: "La misma. Alguien firma 'optimizaciones' y, con cada firma, cae una protección. Silla, persiana, resguardo, extracción... Todo cuesta dinero. Todo tiene un número.", flag: "vega_optimizacion", note: { section: "pistas", heading: "El patrón de las 'optimizaciones'", body: "Vega: los números son 'medidas de optimización' firmadas por alguien; cada una recorta una protección." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_pantalla", category: "Ergonomía: pantallas", severity: 2, discoverable: true, requiredAction: "Pantalla a la altura de los ojos" },
    { id: "h_silla", category: "Ergonomía: postura", severity: 2, discoverable: true, requiredAction: "Silla regulable" },
    { id: "h_luz", category: "Iluminación", severity: 1, discoverable: true, requiredAction: "Reponer persianas, luz sin reflejos" },
    { id: "h_jornada", category: "Jornada y fatiga", severity: 2, discoverable: true, requiredAction: "Pausas y organización del tiempo" },
    { id: "h_carga", category: "Carga mental", severity: 2, discoverable: true, requiredAction: "Priorizar y repartir la carga" },
    { id: "h_muneca", category: "Movimientos repetitivos", severity: 1, discoverable: true, requiredAction: "Reposamuñecas y pausas" },
    { id: "h_temp", category: "Confort térmico", severity: 1, discoverable: true, requiredAction: "Climatización adecuada" },
  ],

  mission: {
    id: "cap4_turno_noche",
    title: "Turno de noche",
    location: "torre",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con la gente de la oficina", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos del puesto", target: "3" },
      { id: "extra", type: "extra", label: "Ajusta el puesto de Marta", hidden: true },
      { id: "report", type: "report", label: "Aplica las medidas", hidden: true },
    ],
  },

  epi: { needed: [], optional: [], overkill: [] },

  extra: {
    triggerId: "puesto",
    type: "ergo",
    label: "Ajustar el puesto de Marta",
    params: [
      { id: "silla", label: "Altura de la silla", labels: ["Muy baja", "Baja", "Correcta (muslos horizontales)", "Alta", "Muy alta"], start: 0, target: 2 },
      { id: "palt", label: "Altura de la pantalla", labels: ["Muy baja", "Baja", "Borde superior a la altura de los ojos", "Alta", "Muy alta"], start: 0, target: 2 },
      { id: "pdist", label: "Distancia de la pantalla", labels: ["Pegada", "Cerca", "A un brazo de distancia", "Lejos", "Muy lejos"], start: 0, target: 2 },
      { id: "teclado", label: "Teclado y muñecas", labels: ["Muñecas muy dobladas", "Algo dobladas", "Muñecas rectas", "Algo hacia arriba", "Muy arriba"], start: 4, target: 2 },
      { id: "luz", label: "Iluminación", labels: ["Oscuro", "Tenue", "Adecuada, sin reflejos", "Brillante", "Deslumbra"], start: 4, target: 2 },
    ],
    wrong: "Aún hay algo que obliga a forzar la postura. Busca el punto neutro: pies apoyados, muslos horizontales, pantalla a la altura de los ojos y a un brazo, muñecas rectas y luz sin reflejos.",
    success: "Puesto en su punto: silla regulada, pantalla a la altura de los ojos y a un brazo, muñecas rectas y luz sin reflejos. Así se trabaja un turno entero sin acabar hecho polvo.",
    note: { section: "expedientes", heading: "Puesto ergonómico ajustado", body: "El puesto se ajusta a la persona: altura de silla y pantalla, distancia, muñecas neutras e iluminación sin reflejos." },
  },

  consequence: {
    title: "Aplicar las medidas",
    lines: {},
    great: "Con el puesto ajustado y la organización arreglada, el turno deja de ser una carrera de obstáculos.",
    ok: "",
    bad: "",
  },

  workId: "aplicar",
  reward: "tarjeta05",
  resolveNotes: [
    { section: "pistas", heading: "Tarjeta 05", body: "Grapada a una carpeta de 'MEDIDAS DE OPTIMIZACIÓN'. La lista de Vega: 03, 05, 07, 11." },
    { section: "expedientes", heading: "Caso: Turno de noche", body: "Daño por ergonomía y organización: puesto sin adaptar, jornada sin pausas, carga mental. Resuelto ajustando el puesto y la organización." },
  ],
  resolveText:
    `<p>Con el puesto ajustado, vuelven las <b>persianas</b> y las <b>sillas regulables</b>, se ordenan las <b>pausas</b> y se prioriza la carga. Aquí no hacían falta EPIs: hacía falta organizar y adaptar. Marta lo nota en el cuello... y en el ánimo.</p>
     <div class="epi-callout">Dentro del cuadrante, una carpeta con el sello 'MEDIDAS DE OPTIMIZACIÓN'. Y grapada, otra tarjeta: <b>05</b>.</div>`,
};
